import { useStore } from "@nanostores/react";
import { useGetUiCapabilitiesForCurrentUser } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { type UiCapabilitiesResponse } from "@vega-api/generated/types/uiCapabilitiesResponse";

import { FeatureFlag } from "./featureFlagEnum";
import {
  legacyPermissionByCapability,
  type UiCapabilities,
  UiCapabilityKey,
  uiCapabilityKeys,
} from "./uiCapabilities";
import {
  $uiCapabilityOverrides,
  applyUiCapabilityOverrides,
} from "./uiCapabilityOverrides";
import { useFeatureFlag } from "./useFeatureFlag";
import {
  type PermissionFlags,
  usePermissionsFlags,
} from "./usePermissionsFlag";

export type { UiCapabilities } from "./uiCapabilities";
export { UiCapabilityKey };

const CAPABILITIES_STALE_TIME_MS = 5 * 60 * 1000;

const resolveCapabilityValue = (
  capability: UiCapabilityKey,
  isApiEnabled: boolean,
  data: UiCapabilitiesResponse | undefined,
  permissionFlags: PermissionFlags,
): boolean => {
  if (isApiEnabled) {
    return data?.[capability]?.some((entry) => entry.enabled) ?? false;
  }

  const legacyPermission = legacyPermissionByCapability[capability];

  return legacyPermission ? permissionFlags[legacyPermission] : false;
};

export const useUiCapabilities = () => {
  const { featureFlag, isLoading: isFeatureFlagLoading } = useFeatureFlag();
  const isApiEnabled = featureFlag[FeatureFlag.GP_AUTH_UI_CAPABILITIES];

  const {
    permissionFlags,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = usePermissionsFlags();

  const {
    data,
    isLoading: isCapabilitiesLoading,
    error: capabilitiesError,
  } = useGetUiCapabilitiesForCurrentUser(undefined, {
    query: {
      enabled: isApiEnabled,
      staleTime: CAPABILITIES_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    },
  });

  const overrides = useStore($uiCapabilityOverrides);

  const capabilities = Object.fromEntries(
    uiCapabilityKeys.map((capability) => [
      capability,
      resolveCapabilityValue(capability, isApiEnabled, data, permissionFlags),
    ]),
  ) as UiCapabilities;

  return {
    capabilities: applyUiCapabilityOverrides(capabilities, overrides),
    isCapabilitiesApiEnabled: isApiEnabled,
    isLoading:
      isFeatureFlagLoading ||
      (isApiEnabled ? isCapabilitiesLoading : isPermissionsLoading),
    error: isApiEnabled ? capabilitiesError : permissionsError,
  };
};
